<script setup>
// Painel do SGP na barra lateral da conversa. Porta do PainelEmbutido do
// eryx-sgp, que antes abria num iframe por cima do Chatwoot.
//
// Montado em ContactPanel.vue (ver custom/docs/PATCHES.md). Quando o recurso SGP
// está desligado para a empresa, a API responde 404 e o painel não se desenha.
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useUISettings } from 'dashboard/composables/useUISettings';
import AccordionItem from 'dashboard/components/Accordion/AccordionItem.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import messages from './i18n';
import { createSgpApi, erroDaApi } from './api';
import SgpDocumento from './SgpDocumento.vue';
import SgpContrato from './SgpContrato.vue';
import SgpFinanceiro from './SgpFinanceiro.vue';
import SgpSuporte from './SgpSuporte.vue';
import SgpManutencoes from './SgpManutencoes.vue';

const props = defineProps({
  conversationId: { type: [Number, String], required: true },
});

const { t } = useI18n({ messages });
const route = useRoute();
const { isContactSidebarItemOpen, toggleSidebarUIState } = useUISettings();

const estado = ref(null);
const habilitado = ref(false);
const carregando = ref(false);
const erro = ref('');
const aba = ref('financeiro');

const api = computed(() =>
  createSgpApi(route.params.accountId, props.conversationId)
);

const ABAS = ['financeiro', 'suporte', 'manutencao'];

const carrega = async () => {
  carregando.value = true;
  erro.value = '';
  try {
    const { data } = await api.value.estado();
    estado.value = data;
    habilitado.value = true;
  } catch (error) {
    // 404: o SGP não existe para esta empresa, e o painel não aparece.
    habilitado.value = error?.response?.status !== 404;
    erro.value = erroDaApi(error, t('NEUXS_SGP.ERRO_GENERICO'));
  } finally {
    carregando.value = false;
  }
};

const atualizaContato = contato => {
  estado.value = { ...estado.value, contato };
};

const documento = computed(() => estado.value?.contato?.documento);
// A troca de contrato recarrega as abas que dependem dele.
const chaveDoContrato = computed(
  () => `${documento.value}-${estado.value?.contato?.contrato}`
);

watch(
  () => props.conversationId,
  () => {
    estado.value = null;
    aba.value = 'financeiro';
    carrega();
  },
  { immediate: true }
);
</script>

<template>
  <div v-if="habilitado" class="px-2 pb-3">
    <AccordionItem
      :title="t('NEUXS_SGP.TITULO')"
      :is-open="isContactSidebarItemOpen('is_neuxs_sgp_open')"
      compact
      @toggle="toggleSidebarUIState('is_neuxs_sgp_open')"
    >
      <div class="flex flex-col gap-3 px-1 text-sm text-n-slate-12">
        <div
          v-if="carregando && !estado"
          class="flex items-center gap-2 text-n-slate-11"
        >
          <Spinner :size="14" />
          {{ t('NEUXS_SGP.CONSULTANDO') }}
        </div>

        <div v-else-if="!estado" class="flex flex-col items-start gap-2">
          <p class="text-n-ruby-11">{{ erro }}</p>
          <Button
            :label="t('NEUXS_SGP.TENTAR_DE_NOVO')"
            size="xs"
            variant="ghost"
            icon="i-lucide-refresh-cw"
            @click="carrega"
          />
        </div>

        <p v-else-if="!estado.configurado" class="text-n-amber-11">
          {{ t('NEUXS_SGP.SEM_CONFIGURACAO') }}
        </p>

        <template v-else>
          <SgpDocumento
            :api="api"
            :contato="estado.contato"
            @salvo="atualizaContato"
          />
          <SgpContrato
            v-if="documento"
            :key="documento"
            :api="api"
            :contato="estado.contato"
            @salvo="atualizaContato"
          />

          <div class="flex gap-1 border-b border-n-weak">
            <button
              v-for="item in ABAS"
              :key="item"
              type="button"
              class="px-2 py-1.5 -mb-px text-xs font-medium border-b-2"
              :class="
                aba === item
                  ? 'border-n-brand text-n-blue-11'
                  : 'border-transparent text-n-slate-11 hover:text-n-slate-12'
              "
              @click="aba = item"
            >
              {{ t(`NEUXS_SGP.ABAS.${item.toUpperCase()}`) }}
            </button>
          </div>

          <!-- Manutenção é do provedor, não do contato: não exige documento. -->
          <SgpManutencoes v-if="aba === 'manutencao'" :api="api" />
          <template v-else-if="documento">
            <SgpFinanceiro
              v-if="aba === 'financeiro'"
              :key="chaveDoContrato"
              :api="api"
              :aceita-botao="estado.aceita_botao"
            />
            <SgpSuporte v-else :key="chaveDoContrato" :api="api" />
          </template>

          <p v-if="estado.host" class="text-xs text-n-slate-10 truncate">
            {{ estado.integracao }} · {{ estado.host }}
          </p>
        </template>
      </div>
    </AccordionItem>
  </div>
</template>
